import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChefHat, ArrowLeft, Clock, Flame, CheckCircle2, User, Phone,
  MapPin, Loader2, RefreshCw, Volume2, VolumeX, Printer, Truck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useThermalPrinter } from "@/hooks/useThermalPrinter";
import type { KitchenTicketData } from "@/lib/escpos";
import type { Tables } from "@/integrations/supabase/types";

type Order = Tables<"orders">;
type OrderItem = Tables<"order_items">;
type OrderWithItems = Order & { items: OrderItem[] };

const COLUMNS = [
  { status: "received" as const, label: "Recebido", icon: Clock, color: "text-primary", bg: "bg-primary/10 border-primary/30" },
  { status: "preparing" as const, label: "Em Preparo", icon: Flame, color: "text-warning", bg: "bg-warning/10 border-warning/30" },
  { status: "ready" as const, label: "Pronto", icon: CheckCircle2, color: "text-success", bg: "bg-success/10 border-success/30" },
  { status: "out_for_delivery" as const, label: "Saiu para Entrega", icon: Truck, color: "text-accent-foreground", bg: "bg-accent/50 border-accent" },
];

const ORDER_TYPE_LABELS: Record<string, string> = {
  dine_in: "Mesa",
  takeout: "Retirada",
  delivery: "Delivery",
  scheduled: "Agendado",
};

const timeAgo = (date: string) => {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
  if (diff < 1) return "agora";
  if (diff < 60) return `${diff} min`;
  return `${Math.floor(diff / 60)}h ${diff % 60}m`;
};

const PainelCozinha = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const printer = useThermalPrinter();
  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [flashNew, setFlashNew] = useState(false);
  const prevOrderIdsRef = useRef<Set<string>>(new Set());
  const initialLoadDone = useRef(false);

  const fetchOrders = useCallback(async (restId: string, retryCount = 0) => {
    const { data: ordersData } = await supabase
      .from("orders")
      .select("*")
      .eq("restaurant_id", restId)
      .in("status", ["received", "preparing", "ready", "out_for_delivery"])
      .order("created_at", { ascending: true });

    if (!ordersData) return;

    const orderIds = ordersData.map((o) => o.id);
    
    // Batch order IDs in chunks of 50 to avoid URL length limits
    let allItems: any[] = [];
    if (orderIds.length > 0) {
      const chunks: string[][] = [];
      for (let i = 0; i < orderIds.length; i += 50) {
        chunks.push(orderIds.slice(i, i + 50));
      }
      const results = await Promise.all(
        chunks.map((chunk) =>
          supabase.from("order_items").select("*").in("order_id", chunk)
        )
      );
      for (const r of results) {
        if (r.data) allItems.push(...r.data);
      }
    }

    const enriched: OrderWithItems[] = ordersData.map((o) => ({
      ...o,
      items: allItems.filter((i) => i.order_id === o.id),
    }));

    // Auto-retry if any order has subtotal but no items (race condition)
    if (retryCount < 2) {
      const missingItems = enriched.some(
        (o) => Number(o.subtotal) > 0 && o.items.length === 0
      );
      if (missingItems) {
        setTimeout(() => fetchOrders(restId, retryCount + 1), 1500);
      }
    }

    // Detect truly new orders (only after initial load)
    if (initialLoadDone.current) {
      const prevIds = prevOrderIdsRef.current;
      const newOrders = enriched.filter((o) => o.status === "received" && !prevIds.has(o.id));
      if (newOrders.length > 0) {
        // Play notification sound
        if (soundEnabled) {
          playNotificationSound();
        }
        // Flash effect
        setFlashNew(true);
        setTimeout(() => setFlashNew(false), 2000);
        // Toast for each new order
        newOrders.forEach((o) => {
          const itemsSummary = o.items.map((i) => `${i.quantity}x ${i.product_name}`).join(", ");
          toast({
            title: `🔔 Novo pedido! #${o.id.slice(0, 6).toUpperCase()}`,
            description: `${o.customer_name || "Cliente"} — ${itemsSummary || "Sem itens"}`,
          });
        });
      }
    }

    prevOrderIdsRef.current = new Set(enriched.map((o) => o.id));
    initialLoadDone.current = true;
    setOrders(enriched);
  }, [soundEnabled, toast]);

  const playNotificationSound = () => {
    try {
      const ctx = new AudioContext();
      // Play a pleasant two-tone chime
      [660, 880].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.15);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.4);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.15);
        osc.stop(ctx.currentTime + i * 0.15 + 0.4);
      });
    } catch {}
  };

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        const { data } = await supabase
          .from("restaurants").select("id").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase
          .from("restaurants").select("id").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurantId(rest.id);
      await fetchOrders(rest.id);
      setLoading(false);
    };
    init();
  }, [navigate, fetchOrders]);

  // Realtime subscription — debounce to avoid race condition where items aren't inserted yet
  useEffect(() => {
    if (!restaurantId) return;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fetchOrders(restaurantId), 800);
    };
    const channel = supabase
      .channel("kitchen-orders")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders", filter: `restaurant_id=eq.${restaurantId}` },
        debouncedFetch
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "order_items" },
        debouncedFetch
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [restaurantId, fetchOrders]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!restaurantId) return;
    const interval = setInterval(() => fetchOrders(restaurantId), 30000);
    return () => clearInterval(interval);
  }, [restaurantId]);

  const moveOrder = async (orderId: string, newStatus: string) => {
    const { error } = await supabase.from("orders").update({ status: newStatus as any }).eq("id", orderId);
    if (error) {
      toast({ title: "Erro ao atualizar pedido", description: error.message, variant: "destructive" });
    } else {
      if (restaurantId) await fetchOrders(restaurantId);
      // WhatsApp notification (fire and forget)
      supabase.functions.invoke('whatsapp-notify-order', {
        body: { order_id: orderId, restaurant_id: restaurantId, status: newStatus },
      }).catch(() => {});
    }
  };

  const printKitchenOrder = async (order: OrderWithItems) => {
    const { fetchCustomerOrderCount, fetchWaiterNameForOrder, parseItemAddons } = await import("@/lib/print-utils");
    
    const [customerOrders, waiterName] = await Promise.all([
      fetchCustomerOrderCount(order.restaurant_id, order.customer_name, order.customer_phone),
      (order.order_type === "dine_in") ? fetchWaiterNameForOrder(order.restaurant_id, order.id) : Promise.resolve(null),
    ]);

    const ticketData: KitchenTicketData = {
      orderId: order.id,
      orderType: order.order_type,
      tableNumber: order.table_number,
      customer: { name: order.customer_name, phone: order.customer_phone, totalOrders: Math.max(0, customerOrders - 1) },
      waiterName,
      items: order.items.map(i => ({
        name: i.product_name,
        quantity: i.quantity,
        unitPrice: Number(i.unit_price),
        totalPrice: Number(i.total_price),
        notes: i.notes,
        addons: parseItemAddons(i.notes),
      })),
      notes: order.notes,
      createdAt: order.created_at,
      scheduledFor: order.scheduled_for,
      paymentStatus: order.payment_status,
      paymentMethod: order.payment_method,
    };
    printer.printKitchenTicket(ticketData);
  };

  const getNextStatus = (current: string, orderType: string) => {
    if (current === "received") return "preparing";
    if (current === "preparing") return "ready";
    if (current === "ready" && orderType === "delivery") return "out_for_delivery";
    if (current === "ready") return "completed";
    if (current === "out_for_delivery") return "completed";
    return null;
  };

  const getNextLabel = (current: string, orderType: string) => {
    if (current === "received") return "Iniciar Preparo";
    if (current === "preparing") return "Marcar Pronto";
    if (current === "ready" && orderType === "delivery") return "Saiu p/ Entrega";
    if (current === "ready") return "Finalizar";
    if (current === "out_for_delivery") return "Entregue";
    return "";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      {/* New order flash overlay */}
      <AnimatePresence>
        {flashNew && (
          <motion.div
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2 }}
            className="fixed inset-0 z-[60] pointer-events-none border-4 border-primary rounded-lg"
          />
        )}
      </AnimatePresence>
      {/* Header */}
      <header className="border-b border-border bg-card shrink-0">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <ChefHat className="h-6 w-6 text-primary" />
            <h1 className="font-display font-bold text-lg">Painel da Cozinha</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
              title={soundEnabled ? "Desativar som" : "Ativar som"}
            >
              {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => restaurantId && fetchOrders(restaurantId)}
              title="Atualizar"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 ml-2">
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span className="text-xs text-muted-foreground hidden sm:inline">Tempo real</span>
            </div>
          </div>
        </div>
      </header>

      {/* Kanban board */}
      <div className="flex-1 overflow-x-auto p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6 min-h-[calc(100vh-8rem)]">
          {COLUMNS.map((col) => {
            const colOrders = orders.filter((o) => o.status === col.status);
            return (
              <div key={col.status} className="flex flex-col">
                {/* Column header */}
                <div className={`flex items-center gap-2 px-4 py-3 rounded-t-xl border ${col.bg}`}>
                  <col.icon className={`h-5 w-5 ${col.color}`} />
                  <span className={`font-display font-semibold ${col.color}`}>{col.label}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {colOrders.length}
                  </Badge>
                </div>

                {/* Cards */}
                <div className="flex-1 bg-card/50 border border-t-0 border-border rounded-b-xl p-3 space-y-3 overflow-y-auto max-h-[calc(100vh-12rem)]">
                  {colOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <col.icon className="h-8 w-8 mb-2 opacity-30" />
                      <p className="text-sm">Nenhum pedido</p>
                    </div>
                  ) : (
                    <AnimatePresence>
                      {colOrders.map((order) => {
                        const next = getNextStatus(order.status, order.order_type);
                        return (
                          <motion.div
                            key={order.id}
                            layout
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="rounded-xl border border-border bg-card p-4 space-y-3 shadow-card"
                          >
                            {/* Order header */}
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-display font-bold text-base">
                                    #{order.id.slice(0, 6).toUpperCase()}
                                  </span>
                                  <Badge variant="outline" className="text-[10px]">
                                    {ORDER_TYPE_LABELS[order.order_type] || order.order_type}
                                  </Badge>
                                  {order.payment_status === "paid" && (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[hsl(142,71%,92%)] text-[hsl(142,71%,30%)] border border-[hsl(142,71%,80%)]">
                                      ✓ Pago
                                    </span>
                                  )}
                                </div>
                                {order.table_number && (
                                  <p className="text-xs text-muted-foreground mt-0.5">Mesa {order.table_number}</p>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {timeAgo(order.created_at)}
                              </span>
                            </div>

                            {/* Customer */}
                            {(order.customer_name || order.customer_phone) && (
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {order.customer_name && (
                                  <span className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    {order.customer_name}
                                  </span>
                                )}
                                {order.customer_phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {order.customer_phone}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Items */}
                            <div className="space-y-1.5">
                              {order.items.length > 0 ? (
                                order.items.map((item) => (
                                  <div key={item.id} className="text-sm">
                                    <div className="flex items-start justify-between">
                                      <span>
                                        <span className="font-semibold text-primary">{item.quantity}x</span>{" "}
                                        {item.product_name}
                                      </span>
                                    </div>
                                    {item.notes && (
                                      <div className="ml-5 text-xs text-accent-foreground/80 bg-accent/50 rounded px-2 py-0.5 mt-0.5">
                                        {item.notes}
                                      </div>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <p className="text-xs text-muted-foreground italic">Sem itens detalhados</p>
                              )}
                            </div>

                            {/* Notes */}
                            {order.notes && (
                              <div className="text-xs bg-warning/10 text-warning border border-warning/20 rounded-lg px-3 py-2">
                                📝 {order.notes}
                              </div>
                            )}

                            {/* Delivery address */}
                            {order.order_type === "delivery" && order.delivery_address && (
                              <div className="text-xs text-muted-foreground flex items-start gap-1">
                                <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                                <span className="truncate">{order.delivery_address}</span>
                              </div>
                            )}

                            {/* Total + Actions */}
                            <div className="flex items-center justify-between pt-2 border-t border-border">
                              <span className="font-display font-bold">
                                R$ {Number(order.total).toFixed(2).replace(".", ",")}
                              </span>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={() => printKitchenOrder(order)}
                                  title="Imprimir comanda"
                                >
                                  <Printer className="h-3.5 w-3.5" />
                                </Button>
                                {next && (
                                  <Button
                                    size="sm"
                                    onClick={() => moveOrder(order.id, next)}
                                    className={
                                      next === "preparing"
                                        ? "bg-warning text-warning-foreground hover:bg-warning/90"
                                        : next === "ready"
                                        ? "bg-success text-success-foreground hover:bg-success/90"
                                        : "bg-muted text-muted-foreground hover:bg-muted/90"
                                    }
                                  >
                                    {getNextLabel(order.status, order.order_type)}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PainelCozinha;
